/* using static version of t, because hooks are not accessible in leaflet-tooltip */
import { OpenInNew as OpenInNewIcon } from "@mui/icons-material";
import { isNil } from "lodash";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
  ICarTrackingInfo,
  NO_VALUE_DISPLAY,
  resolveLiveMapTooltipActualArrivalTime,
  resolveLiveMapTooltipDriverName,
  resolveLiveMapTooltipPassengersCount,
  resolveTrackingPopoverDelay,
  shouldShowCourseInTrackingPopover,
  useFormats,
} from "@ryde/shared";

import { MapTooltipCourseRow } from "./map-tooltip-course-row";
import {
  MapTooltipDetailRow,
  MapTooltipDriverRow,
} from "./map-tooltip-detail-row";
import { RIDE_IN_DETOUR_GRID_EMOJI } from "./map-tooltip.constants";
import {
  MapTooltipStyled,
  MapVehicleTrackingPopupGlobalStyle,
} from "./map-tooltip.styled";
import { getTrackingDelayValueClass } from "./map-tooltip.utils";

const TIME_ARROW_EMOJI = "➡️";

type MapTooltipProps = ICarTrackingInfo & {
  /** Live-map `?debugMode` — matches course marker synthetic codes. */
  debugMode?: boolean;
  /**
   * Opens this ride's edit panel as an overlay drawer (TLV [Ver-2] vehicle popup).
   * Resolved by the caller (has Recoil access); omitted when the user lacks ride view/edit permission.
   */
  onOpenRide?: () => void;
};

/* THIS COMPONENT IS RENDERED IN LEAFLET MAP,
NO STATE MANAGEMENT LIKE RECOIL IS ACCESSIBLE INSIDE THIS COMPONENT */
export const MapTooltip: FC<MapTooltipProps> = ({
  rideId,
  supplierName,
  vehicleName,
  vehicleTypeName,
  actualArrivalTime,
  plannedArrivalTime,
  timeLate,
  metadata,
  driverName,
  driverPhone,
  externalDriverName,
  externalDriverPhone,
  courseId,
  courseName,
  courseCode,
  courseRouteType,
  originStationName,
  destinationStationName,
  scheduleNumber,
  passengersCount,
  trackingLastUpdate,
  debugMode,
  onOpenRide,
}) => {
  const { t } = useTranslation();
  const { formatTimeNormalized, formatDateTimeNormalized } = useFormats();
  // Priority: journey-verification (driver-app, stamped on Start Ride) > driver-app self-reported login > ride's manually-assigned driver.
  const name = resolveLiveMapTooltipDriverName({
    debugMode,
    driverName: externalDriverName || metadata?.driverInfo?.name || driverName,
    rideId,
  });
  const phone =
    externalDriverPhone || metadata?.driverInfo?.phone || driverPhone;
  const resolvedPassengersCount = resolveLiveMapTooltipPassengersCount({
    debugMode,
    passengersCount,
    rideId,
  });
  const resolvedActualArrivalTime = useMemo(
    () =>
      resolveLiveMapTooltipActualArrivalTime({
        debugMode,
        actualArrivalTime,
        plannedArrivalTime,
        trackingLastUpdate,
      }),
    [actualArrivalTime, debugMode, plannedArrivalTime, trackingLastUpdate],
  );
  const isDebugSyntheticActual = Boolean(
    debugMode && !actualArrivalTime && resolvedActualArrivalTime,
  );
  const popoverDelay = useMemo(
    () =>
      resolveTrackingPopoverDelay({
        plannedArrivalTime,
        actualArrivalTime: resolvedActualArrivalTime,
        timeLate,
        requireCoherentPlannedActualPair: isDebugSyntheticActual,
      }),
    [
      isDebugSyntheticActual,
      resolvedActualArrivalTime,
      plannedArrivalTime,
      timeLate,
    ],
  );

  const delayLabel = useMemo(
    () =>
      popoverDelay.kind === "early" ? t("rideMap.EARLIER") : t("rideMap.DELAY"),
    [popoverDelay.kind, t],
  );

  const delayDisplayValue: string = useMemo(() => {
    switch (popoverDelay.kind) {
      case "late":
        return t("rideMap.DELAY_MIN", { delay: popoverDelay.minutes });
      case "early":
        return popoverDelay.minutes
          ? t("rideMap.DELAY_MIN", { delay: popoverDelay.minutes })
          : NO_VALUE_DISPLAY;
      default:
        return NO_VALUE_DISPLAY;
    }
  }, [popoverDelay, t]);

  const delayValueClass = getTrackingDelayValueClass(popoverDelay.kind);

  const showCourseRow = shouldShowCourseInTrackingPopover({ courseId });

  return (
    <>
      <MapVehicleTrackingPopupGlobalStyle />
      <MapTooltipStyled>
        <div className="time-strip">
          <div className="time-cell">
            <span className="time-cell__label">{t("rideMap.PLANNED")}</span>
            <span className="time-cell__value">
              {formatTimeNormalized(plannedArrivalTime)}
            </span>
          </div>
          <span className="time-strip__arrow" aria-hidden>
            {TIME_ARROW_EMOJI}
          </span>
          <div className="time-cell">
            <span className="time-cell__label">{t("rideMap.ACTUAL")}</span>
            <span className="time-cell__value time-cell__value--actual">
              {formatTimeNormalized(resolvedActualArrivalTime)}
            </span>
          </div>
          <div className="time-cell">
            <span className="time-cell__label">{delayLabel}</span>
            <span className={delayValueClass}>{delayDisplayValue}</span>
          </div>
        </div>
        {metadata?.inDetour === true && (
          <div className="detour-banner">
            <span className="detour-banner__emoji" aria-hidden>
              {RIDE_IN_DETOUR_GRID_EMOJI}
            </span>
            <span className="detour-banner__label">
              {t("rideMap.DETOUR", { defaultValue: "Detour" })}
            </span>
          </div>
        )}

        <div className="details">
          {showCourseRow && courseId !== undefined && courseId !== null && (
            <MapTooltipCourseRow
              courseName={courseName}
              courseCode={courseCode}
              courseRouteType={courseRouteType}
            />
          )}
          {scheduleNumber && (
            <div className="detail-row">
              <span className="detail-row__label">
                {t("rideMap.SCHEDULE_NUM")}
              </span>
              <span className="detail-row__value">{scheduleNumber}</span>
            </div>
          )}
          <MapTooltipDetailRow
            label={t("rideMap.FROM")}
            text={originStationName}
          />
          <MapTooltipDetailRow
            label={t("rideMap.TO")}
            text={destinationStationName}
          />
          <MapTooltipDriverRow
            label={t("rideMap.DRIVER")}
            name={name}
            phone={phone}
          />
          <MapTooltipDetailRow
            label={t("rideMap.VEHICLE_TYPE")}
            text={vehicleTypeName}
          />
          <MapTooltipDetailRow
            label={t("rideMap.VEHICLE")}
            text={vehicleName}
          />
          <MapTooltipDetailRow
            label={t("rideMap.SUPPLIER")}
            text={supplierName}
          />
        </div>

        {!isNil(resolvedPassengersCount) || trackingLastUpdate ? (
          <div className="details-bottom">
            {!isNil(resolvedPassengersCount) && (
              <div className="detail-row">
                <span className="detail-row__label">
                  {t("rideMap.PASSENGERS")}
                </span>
                <span className="detail-row__value">
                  {resolvedPassengersCount}
                </span>
              </div>
            )}
            {trackingLastUpdate && (
              <div className="detail-row detail-row--last-update">
                <span className="detail-row__label">
                  {t("rideMap.LAST_UPDATE")}
                </span>
                <span className="detail-row__value">
                  {formatDateTimeNormalized(trackingLastUpdate)}
                </span>
                {Boolean(onOpenRide) && (
                  <button
                    type="button"
                    className="open-ride-icon-button"
                    onClick={onOpenRide}
                    aria-label={t("rideMap.OPEN_RIDE", {
                      defaultValue: "Open Ride",
                    })}
                  >
                    <OpenInNewIcon />
                  </button>
                )}
              </div>
            )}
          </div>
        ) : null}
      </MapTooltipStyled>
    </>
  );
};
